import { UpgradeSubscriptionModal } from '@components'

import { NavItemSeparator, sanitizeNavItemSeparators, TableStaticActions, useModal } from '@billy/design-system'
import Cookies from 'js-cookie'
import { ReactElement, useCallback, useMemo, useState } from 'react'
import { useLocation, useNavigate } from 'react-router'

import { queryClient } from '../../../../../../../../config/queryClient'
import { CookieKeys } from '../../../../../../../../enums/cookieKeys'
import { QueryKeys } from '../../../../../../../../enums/queryKeys'
import { TrackingContext } from '../../../../../../../../enums/trackingContext'
import { useSegment } from '../../../../../../../../hooks'
import { getOrganizationViewUrl } from '../../../../../../../../utils/routing/getOrganizationViewUrl'
import { reactRoute } from '../../../../../../../../utils/routing/reactRoute'
import { CancelInvoiceModal } from '../../../../../../elements/CancelInvoiceModal'
import { DeleteDraftConfirmationModal } from '../../../../../../elements/DeleteDraftConfirmationModal'
import { SendAsEmailModal } from '../../../../../../elements/SendAsEmailModal'
import { SendInvoiceAsEInvoiceModal } from '../../../../../../elements/SendInvoiceAsEInvoiceModal'
import { DownloadDocumentFileType } from '../../../../../../enums/downloadDocumentFileType'
import { InvoiceBillingStage } from '../../../../../../enums/invoiceBillingStage'
import { InvoiceDocumentType } from '../../../../../../enums/invoiceDocumentType'
import { SendAsEmailModalVariant } from '../../../../../../enums/sendAsEmailModalVariant'
import { ZervantLocale } from '../../../../../../enums/zervantLocale'
import { useMissingEInvoiceDetailsForInvoice } from '../../../../../../hooks/eInvoices/useMissingEInvoiceDetailsForInvoice'
import { useSendAsEInvoiceAction } from '../../../../../../hooks/sendAsEInvoice/useSendAsEInvoiceAction'
import { useSendAsEmail } from '../../../../../../hooks/sendAsEmail/useSendAsEmail'
import { useDeleteSalesDocumentDraft } from '../../../../../../hooks/useDeleteSalesDocumentDraft'
import { useDownloadInvoice } from '../../../../../../hooks/useDownloadInvoice'
import { useFetchInvoice } from '../../../../../../hooks/useFetchInvoice'
import { useInvoiceActionFlags } from '../../../../../../hooks/useInvoiceActionFlags'
import { useInvoiceStatusActions } from '../../../../../../hooks/useInvoiceStatusActions'
import { SendAsEmailForm } from '../../../../../../types/sendAsEmailForm'
import { getCancelDepositInvoiceEligibility } from '../../../../../../utils/depositInvoices/getCancelDepositInvoiceEligibility'
import { getInvoiceDocumentRouteBase } from '../../../../../../utils/getInvoiceDocumentRouteBase'
import { getInvoiceDraftEditRoute } from '../../../../../../utils/getInvoiceDraftEditRoute'
import { getInvoiceQueryKey } from '../../../../../../utils/getInvoiceQueryKey'
import { getTrackingDocumentType } from '../../../../../../utils/getTrackingDocumentType'
import { isDocumentTypeCreditNote } from '../../../../../../utils/isDocumentTypeCreditNote'
import { isDocumentTypeInvoice } from '../../../../../../utils/isDocumentTypeInvoice'
import { BlockCancelInvoiceActionModals } from '../../../../../BlockCancelInvoiceActionModals'
import { SendEInvoiceRequirementsModal } from '../../../../../SendEInvoiceRequirementsModal'
import { useInvoicesList } from '../../../../context/invoicesList'
import { InvoicesTableStaticActionType } from '../../../../enums/invoicesTableStaticActionType'
import { InvoicesTableStaticActionItem } from '../../../../types/invoicesTableStaticActionItem'

interface InvoicesTableStaticActionsProps {
  billingStage?: InvoiceBillingStage
  documentLocale: ZervantLocale
  documentType: InvoiceDocumentType
  invoiceNumber: string | undefined
  items: InvoicesTableStaticActionItem[]
  invoiceId: string
}

export const InvoicesTableStaticActions = ({
  billingStage,
  documentLocale,
  documentType,
  invoiceId,
  invoiceNumber,
  items,
}: InvoicesTableStaticActionsProps): ReactElement => {
  const [isFetchEnabled, setIsFetchEnabled] = useState(false)
  const navigate = useNavigate()
  const location = useLocation()
  const { track } = useSegment()
  const { options, organization } = useInvoicesList()

  // Fetching the invoice to determine the action flags which requires the invoice data from
  // Billy and Zervant. We only fetch those data when the user opens the static actions menu.
  const {
    invoice,
    isError: isInvoiceFetchError,
    isFetching: isInvoiceFetching,
  } = useFetchInvoice(documentType, isFetchEnabled ? invoiceId : undefined)
  const {
    canRegisterPayment,
    isCancellable,
    isDuplicable,
    isExternalInvoiceLoading,
    isMarkableAsSent,
    isUnmarkableAsSent,
  } = useInvoiceActionFlags({
    invoice,
    documentType,
  })

  const resolvedDocumentLocale = invoice?.locale ?? documentLocale
  const cancelDepositInvoiceEligibility = getCancelDepositInvoiceEligibility(invoice)

  const missingEInvoiceDetails = useMissingEInvoiceDetailsForInvoice(invoice)
  const {
    handleSendAsEInvoice,
    handleSendAsEInvoiceModalSubmit,
    isProcessingSendingAsEInvoice,
    sendAsEInvoiceModalId,
    sendEInvoiceRequirementsModalId,
    shouldDisplaySendAsEInvoiceOption,
    upgradeSubscriptionModalId,
  } = useSendAsEInvoiceAction({
    customer: invoice?.customer,
    documentId: invoiceId,
    documentStatus: invoice?.status?.status,
    documentType,
    eInvoiceAddressingLine: invoice?.eInvoiceAddressingLine,
    missingEInvoiceDetails,
    onSuccess: () => {
      queryClient.invalidateQueries(getInvoiceQueryKey(invoiceId, documentType))
      queryClient.invalidateQueries(QueryKeys.InvoicesZervant)
      queryClient.invalidateQueries(QueryKeys.ClientInvoicesZervant)
    },
    trackingEventName: 'XXX - Invoicing Core - Invoices List - Quick Actions - Send as e-invoice clicked',
    trackingPayload: {
      documentType: getTrackingDocumentType({ billingStage: invoice?.billingStage, documentType }),
      invoiceId,
    },
  })

  const isDocumentInvoiceOrCreditNote = isDocumentTypeInvoice(documentType) || isDocumentTypeCreditNote(documentType)
  const isLoading =
    isFetchEnabled &&
    isDocumentInvoiceOrCreditNote &&
    !isInvoiceFetchError &&
    (!invoice || isInvoiceFetching || isExternalInvoiceLoading)

  const { markInvoiceAsSent, unmarkInvoiceAsSent } = useInvoiceStatusActions({ documentType, invoiceId })

  const handleMenuOpen = useCallback(() => setIsFetchEnabled(true), [])

  const routeBase = getInvoiceDocumentRouteBase(documentType)
  const editUrl = getOrganizationViewUrl(
    organization?.url,
    getInvoiceDraftEditRoute(billingStage, invoiceId, documentType),
  )
  const duplicateUrl = getOrganizationViewUrl(
    organization?.url,
    documentType === InvoiceDocumentType.InvoiceDraft
      ? reactRoute.organization.invoices.getDuplicateDocumentDraftRoute(routeBase, invoiceId)
      : reactRoute.organization.invoices.getDuplicateDocumentRoute(routeBase, invoiceId),
  )
  const creditNoteUrl = getOrganizationViewUrl(
    organization?.url,
    reactRoute.organization.invoices.getCreditNoteFromInvoiceRoute(invoiceId),
  )
  const registerPaymentUrl = getOrganizationViewUrl(
    organization?.url,
    reactRoute.organization.invoices.getViewInvoiceRoute(invoiceId, documentType),
  )
  const deleteDraftModalId = `${invoiceId}-delete-draft-confirmation`
  const sendByEmailModalId = `${invoiceId}-send-by-email`
  const cancelInvoiceModalId = `${invoiceId}-cancel-invoice`

  const { open: openDeleteDraftModal, close: closeDeleteDraftModal } = useModal(deleteDraftModalId)
  const { open: openSendByEmailModal, close: closeSendByEmailModal } = useModal(sendByEmailModalId)
  const { open: openCancelInvoiceModal } = useModal(cancelInvoiceModalId)

  const { download, isDownloading } = useDownloadInvoice()
  const { deleteSalesDocumentDraft, isProcessing: isDeletingDraft } = useDeleteSalesDocumentDraft({
    onSuccess: closeDeleteDraftModal,
  })
  const { sendAsEmail, isProcessing: isSendingByEmail } = useSendAsEmail(SendAsEmailModalVariant.Invoice, {
    onSuccess: () => {
      queryClient.invalidateQueries(getInvoiceQueryKey(invoiceId, documentType))
      queryClient.invalidateQueries(QueryKeys.InvoicesZervant)
      queryClient.invalidateQueries(QueryKeys.ClientInvoicesZervant)
      closeSendByEmailModal()
    },
  })

  const effectiveItems = useMemo(() => {
    if (isLoading) {
      return []
    }

    const actionGates: Partial<Record<InvoicesTableStaticActionType, boolean>> = {
      [InvoicesTableStaticActionType.Duplicate]: documentType === InvoiceDocumentType.InvoiceDraft || isDuplicable,
      [InvoicesTableStaticActionType.CancelInvoice]: isCancellable,
      [InvoicesTableStaticActionType.MarkAsSent]: !!isMarkableAsSent,
      [InvoicesTableStaticActionType.UnmarkAsSent]: !!isUnmarkableAsSent,
      [InvoicesTableStaticActionType.RegisterPayment]: !!canRegisterPayment,
      [InvoicesTableStaticActionType.SendAsEInvoice]: shouldDisplaySendAsEInvoiceOption,
    }

    const readonlyGates: Partial<Record<InvoicesTableStaticActionType, boolean>> = {
      [InvoicesTableStaticActionType.DownloadPDF]: isDownloading,
      [InvoicesTableStaticActionType.SendAsEInvoice]: isProcessingSendingAsEInvoice,
    }

    return sanitizeNavItemSeparators(
      items
        .filter((item) => actionGates[item.value] ?? true)
        .map((item) => (readonlyGates[item.value] ? { ...item, readonly: true } : item)),
    )
  }, [
    canRegisterPayment,
    documentType,
    isCancellable,
    isDownloading,
    isDuplicable,
    isLoading,
    isMarkableAsSent,
    isProcessingSendingAsEInvoice,
    isUnmarkableAsSent,
    items,
    shouldDisplaySendAsEInvoiceOption,
  ])

  const handleDeleteDraftConfirm = useCallback(() => {
    deleteSalesDocumentDraft({
      approvedEstimateId: invoice?.approvedEstimateId?.toString(),
      documentId: Number(invoiceId),
      documentType,
    })
  }, [deleteSalesDocumentDraft, documentType, invoice?.approvedEstimateId, invoiceId])

  const handleSendAsEmailFormSubmit = useCallback(
    (formData: SendAsEmailForm) => {
      sendAsEmail({ documentType, formData, invoiceId: Number(invoiceId) })
    },
    [documentType, invoiceId, sendAsEmail],
  )

  const navigateToUrl = useCallback(
    (url: string) => {
      if (options.persistFiltersInUrl) {
        Cookies.set(CookieKeys.InvoicingInvoicesListQuery, location.search)
      }
      navigate(url)
    },
    [location.search, navigate, options.persistFiltersInUrl],
  )

  const handleStaticActionSelect = useCallback(
    (_id: string, value: InvoicesTableStaticActionType | NavItemSeparator) => {
      switch (value) {
        case InvoicesTableStaticActionType.Edit:
          navigateToUrl(editUrl)
          break

        case InvoicesTableStaticActionType.Duplicate:
          navigateToUrl(duplicateUrl)
          break

        case InvoicesTableStaticActionType.DownloadPDF:
          download({
            documentLocale: resolvedDocumentLocale,
            documentType,
            downloadDocumentFileType: DownloadDocumentFileType.PDF,
            invoiceId,
            invoiceNumber,
          })
          break

        case InvoicesTableStaticActionType.DeleteDraft:
          openDeleteDraftModal()
          break

        case InvoicesTableStaticActionType.SendByEmail:
          openSendByEmailModal()
          break

        case InvoicesTableStaticActionType.SendAsEInvoice:
          handleSendAsEInvoice()
          break

        case InvoicesTableStaticActionType.CancelInvoice:
          openCancelInvoiceModal()
          break

        case InvoicesTableStaticActionType.MarkAsSent:
          markInvoiceAsSent()
          break

        case InvoicesTableStaticActionType.UnmarkAsSent:
          unmarkInvoiceAsSent()
          break

        case InvoicesTableStaticActionType.RegisterPayment:
          track('XXX - Invoicing Core - Invoices List - Quick Actions - Register payment clicked', {
            documentType: getTrackingDocumentType({ billingStage: invoice?.billingStage, documentType }),
            invoiceId,
          })
          // This feature is added as a experiment where Invoice View's Register Payment modal
          // is deep-linked from the Invoices List quick actions. The usage data will determine
          // if this quick action should be removed or properly built without deep-linking.
          Cookies.set(CookieKeys.InvoiceViewRegisterPaymentRequest, invoiceId)
          navigateToUrl(registerPaymentUrl)
          break
      }
    },
    [
      download,
      documentType,
      duplicateUrl,
      editUrl,
      handleSendAsEInvoice,
      invoice,
      invoiceId,
      invoiceNumber,
      markInvoiceAsSent,
      navigateToUrl,
      openCancelInvoiceModal,
      openDeleteDraftModal,
      openSendByEmailModal,
      registerPaymentUrl,
      resolvedDocumentLocale,
      track,
      unmarkInvoiceAsSent,
    ],
  )

  return (
    <>
      <TableStaticActions
        dropdownFetching={isLoading}
        items={effectiveItems}
        onOpen={handleMenuOpen}
        onSelect={handleStaticActionSelect}
      />
      <DeleteDraftConfirmationModal
        documentType={documentType}
        id={deleteDraftModalId}
        isDeleting={isDeletingDraft}
        onOk={handleDeleteDraftConfirm}
      />
      <CancelInvoiceModal
        documentType={documentType}
        id={cancelInvoiceModalId}
        invoiceId={invoiceId}
        onConfirmCreditNote={() => navigateToUrl(creditNoteUrl)}
      />
      <BlockCancelInvoiceActionModals
        cancelDepositInvoiceEligibility={cancelDepositInvoiceEligibility}
        invoiceId={invoiceId}
      />
      <SendAsEmailModal
        contactId={invoice?.customer?.externalContactId ?? undefined}
        documentLocale={resolvedDocumentLocale}
        documentType={documentType}
        id={sendByEmailModalId}
        invoiceNumber={invoiceNumber ?? ''}
        isSubmitting={isSendingByEmail}
        onSubmit={handleSendAsEmailFormSubmit}
        variant={SendAsEmailModalVariant.Invoice}
        view="invoiceList"
      />
      <SendEInvoiceRequirementsModal
        cancelInvoiceModalId={cancelInvoiceModalId}
        id={sendEInvoiceRequirementsModalId}
        missingEInvoiceDetails={missingEInvoiceDetails}
      />
      <SendInvoiceAsEInvoiceModal
        customer={invoice?.customer}
        id={sendAsEInvoiceModalId}
        missingEInvoiceDetails={missingEInvoiceDetails}
        onSubmit={handleSendAsEInvoiceModalSubmit}
        view="invoiceList"
      />
      <UpgradeSubscriptionModal
        id={upgradeSubscriptionModalId}
        shouldRedirectToZervantPlanPage
        trackingContext={TrackingContext.ShineEInvoicing}
      />
    </>
  )
}
